import React, { ForwardRefRenderFunction, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useForm, FormProvider, useWatch, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { sortTablesByUpperCaseFirst } from 'constants/global';
import {
  AGGREGATE_FUNCTION_TYPE_MAP,
  AggregateFunctionType,
  AggregateItem,
  ASCENDING_DIRECTION,
  DESCENDING_DIRECTION,
  FilterItem,
  FilterOperator,
  getAggregateFunctionNumber,
  getAggregateFunctionTypeString,
  getDisplayTypeFromDataType,
  getOperatorString,
  parseQualifiedColumn,
  REPORT_FILTER_SECTIONS,
  ReportFilterSectionId,
  SortDirection,
  typeStringMap,
  VisualizationFormState,
  VisualizationType,
  visualizationTypeMap,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import {
  ApiJoinRow,
  DisplayColumn,
  JoinRow,
  QueryFilter,
  ReportTemplateConfiguration,
  SortingRule,
  VisualizationItemPayload,
} from 'interfaces/report-templates.interface';
import { Modal, SelectFormInput } from 'shared-components';

import CreateReportTemplateAggregateModal from './CreateReportTemplateAggregateModal';
import CreateReportTemplateFilterItem from './CreateReportTemplateFilterItem';
import CreateReportTemplateFiltersModal from './CreateReportTemplateFiltersModal';
import CreateReportTemplateGroupingModal from './CreateReportTemplateGroupingModal';
import CreateReportTemplateJoinModal from './CreateReportTemplateJoinModal';
import CreateReportTemplateSortByModal from './CreateReportTemplateSortByModal';
import CreateReportTemplateVisualizationBlock from './CreateReportTemplateVisualizationBlock';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  onValidationChange?: (isValid: boolean) => void;
  initialVisualization?: VisualizationFormState | null;
  initialValues?: ReportTemplateConfiguration | null;
};

export type ReportTemplateFiltersBlockHandle = {
  resetForm: () => void;
  getCurrentConfiguration: () => Record<string, unknown> | null;
  getSelectedColumnsData: () => ReportingColumn[];
  setInitialConfiguration: (data: ReportTemplateConfiguration) => void;
  getTemplateName: () => string;
  buildSavePayload: (name: string) => Record<string, unknown> | null;
};

const CreateReportTemplateFiltersBlock: ForwardRefRenderFunction<ReportTemplateFiltersBlockHandle, Props> = (
  { selectedColumnsData, onValidationChange, initialVisualization, initialValues },
  ref
) => {
  const { t } = useTranslation();
  const [templateName, setTemplateName] = useState<string>('');
  const query = initialValues?.query;
  const [count, setCount] = useState(0);

  const initialGroupingValue = (() => {
    const gb = query?.groupBy;
    if (!gb) {
      return null;
    }
    return gb.name ? { label: gb.name, value: gb.name } : null;
  })();

  const initialSortingValue = (() => {
    const s = query?.sorting;
    if (!s?.[0]) {
      return null;
    }
    return s[0].column?.name ? { label: s[0].column.name, value: s[0].column.name } : null;
  })();

  const initialFiltersValue = (() => {
    const f = query?.filters;
    if (!f || f.length === 0) {
      return [] as FilterItem[];
    }
    return f.map((filter: QueryFilter, index: number) => ({
      id: filter.id ?? String(index + 1),
      column: filter.column?.name ?? '',
      condition: filter.condition,
      value: filter.value ?? '',
      locked: filter.isLocked ?? false,
      operator: index > 0 ? (filter.operator as FilterItem['operator']) : undefined,
    }));
  })();

  type FormValues = {
    name: string;
    selectedTable: { label: string; value: string } | null;
    grouping: { label: string; value: string } | null;
    sorting: { label: string; value: string } | null;
    sortingDirection: { label: string; value: SortDirection } | null;
    filters: FilterItem[];
    visualization: VisualizationFormState | null;
  };

  const joinRowToApiJoin = (j: JoinRow): ApiJoinRow => {
    const findMetaFor = (tableName?: string, columnName?: string) => {
      if (!columnName) {
        return undefined;
      }
      if (tableName && tableName !== 'Previous Results') {
        return selectedColumnsData?.find((c) => c.tableName === tableName && c.name === columnName);
      }
      return selectedColumnsData?.find((c) => c.name === columnName);
    };

    const leftMeta = findMetaFor(j.leftTable, j.leftColumn);
    const rightMeta = findMetaFor(j.rightTable, j.rightColumn);

    let columnPayload: ApiJoinRow['column'] | undefined;
    if (leftMeta) {
      columnPayload = {
        name: leftMeta.name,
        tableName: leftMeta.tableName,
        displayType: leftMeta.displayType ?? '',
        type: leftMeta.columnType ?? 3,
      };
    } else if (j.leftColumn) {
      columnPayload = {
        name: j.leftColumn,
        tableName: j.leftTable || selectedTable || '',
        displayType: j.leftDisplayType || '',
        type: 3,
      };
    }

    let joinColumnPayload: ApiJoinRow['joinColumn'] | undefined;
    if (rightMeta) {
      joinColumnPayload = {
        name: rightMeta.name,
        tableName: rightMeta.tableName,
        displayType: rightMeta.displayType ?? '',
        type: rightMeta.columnType ?? 3,
      };
    } else if (j.rightColumn) {
      joinColumnPayload = {
        name: j.rightColumn,
        tableName: j.rightTable || '',
        displayType: '',
        type: 3,
      };
    }

    return {
      id: j.id,
      column: columnPayload,
      joinColumn: joinColumnPayload,
    };
  };

  const buildAggregatesPayload = (items: AggregateItem[] | undefined, tableName: string | null) => {
    if (!items || items.length === 0) {
      return {
        displayColumns: [] as DisplayColumn[],
        aggregates: [] as { func: string; column: string | undefined }[],
      };
    }

    const displayColumns = items.map((agg) => {
      const isCount = agg.func === 'Count';
      const columnName = isCount ? 'Count' : agg.column ?? '';
      const functionTypeNumber = agg.func ? AGGREGATE_FUNCTION_TYPE_MAP[agg.func] : 3;
      const aggregateId = isCount
        ? `${tableName}.${'Count'}.${functionTypeNumber}`
        : `${tableName}.${agg.column}.${functionTypeNumber}`;

      return {
        id: aggregateId,
        customName: null,
        aggregateFunctionType: String(functionTypeNumber),
        displayName: isCount ? 'Count' : `${agg.func} ${agg.column}`,
        tableName: tableName ?? '',
        name: columnName,
        type: 3,
        displayType: 'BIGINT',
      } as DisplayColumn;
    });

    const aggregatesPayload = items.map((agg) => ({
      func: agg.func,
      column: agg.func === 'Count' ? 'Count' : agg.column,
    }));

    return { displayColumns, aggregates: aggregatesPayload };
  };

  const buildVizDisplayColumnsForCurrent = (viz: VisualizationFormState): DisplayColumn[] => {
    if (viz.type === 'Table') {
      const columns = (viz.columns ?? [])
        .map((colName: string) => {
          const { table, column } = parseQualifiedColumn(String(colName));
          const tbl = table ?? selectedTable;
          const meta = selectedColumnsData?.find((c) => c.name === column && c.tableName === tbl);
          if (!meta) {
            return null;
          }
          return {
            id: `${meta.tableName}.${meta.name}`,
            customName: null,
            aggregateFunctionType: null,
            displayName: meta.displayName ?? meta.name,
            tableName: meta.tableName,
            name: meta.name,
            type: meta.columnType,
            displayType: meta.displayType ?? typeStringMap[meta.columnType],
          } as DisplayColumn;
        })
        .filter(Boolean) as DisplayColumn[];
      return columns;
    }

    return [];
  };

  const buildSortingPayload = (): SortingRule[] | undefined => {
    if (!sorting) {
      return undefined;
    }
    const columnMeta = selectedColumnsData?.find((c) => c.name === sorting.column && c.tableName === selectedTable);
    const columnPayload = {
      name: columnMeta?.name ?? sorting.column,
      displayName: columnMeta?.displayName ?? sorting.column,
      tableName: selectedTable ?? '',
      type: columnMeta?.columnType ?? 3,
      displayType: columnMeta?.displayType ?? typeStringMap[columnMeta?.columnType ?? 3],
    };
    return [
      {
        column: columnPayload as unknown as SortingRule['column'],
        direction: sorting.direction === ASCENDING_DIRECTION ? 1 : 2,
        order: 1,
      },
    ];
  };

  const AGGREGATE_KEYWORDS: AggregateFunctionType[] = [
    'Sum of',
    'Count',
    'Distinct count of',
    'Average of',
    'Maximum of',
    'Minimum of',
  ];

  const detectAggregateFunction = (displayName?: string | null): AggregateFunctionType | null => {
    if (!displayName) {
      return null;
    }
    const found = AGGREGATE_KEYWORDS.find((k) => displayName.includes(k));
    return (found as AggregateFunctionType) ?? null;
  };

  const createReportTemplateMethods = useForm<FormValues>({
    defaultValues: {
      name: initialValues?.name ?? '',
      selectedTable: query?.tableName ? { label: query?.tableName, value: query?.tableName } : null,
      grouping: initialGroupingValue,
      visualization: initialVisualization ?? null,
      sorting: initialSortingValue,
      filters: initialFiltersValue,
    },
  });
  const { control, reset, setValue, watch } = createReportTemplateMethods;

  const watchedTable = watch('selectedTable');
  const watchedSelectedTable = useWatch({
    control,
    name: 'selectedTable',
  });

  const selectedTable = watchedSelectedTable?.value ?? null;
  const [activeModal, setActiveModal] = useState<ReportFilterSectionId | null>(null);
  const [savedJoins, setSavedJoins] = useState<JoinRow[]>([]);
  const [grouping, setGrouping] = useState<VisualizationItemPayload | null>(query?.groupBy ?? null);
  const [aggregates, setAggregates] = useState<{ func: AggregateFunctionType; column?: string }[]>([]);
  const [visualization, setVisualization] = useState<VisualizationFormState | null>(null);
  const idCounter = useRef(0);

  const getNextId = () => {
    idCounter.current += 1;
    return idCounter.current;
  };

  const convertApiJoinToJoinRow = (apiJoin: ApiJoinRow, idx: number, baseTable?: string): JoinRow => {
    const isFirst = idx === 0;
    const leftTableRaw = apiJoin.column?.tableName;
    const leftTable = isFirst ? leftTableRaw ?? baseTable ?? '' : 'Previous Results';

    return {
      id: apiJoin.id ?? getNextId(),
      leftTable,
      rightTable: apiJoin.joinColumn?.tableName || '',
      leftColumn: apiJoin.column?.name || '',
      rightColumn: apiJoin.joinColumn?.name || '',
      leftDisplayType: apiJoin.column?.displayType,
    };
  };

  const getColumnInfo = (colName: string, tbl: string) => {
    const meta = selectedColumnsData?.find((c) => c.name === colName && c.tableName === tbl);
    if (!meta) {
      return null;
    }
    return {
      name: meta.name,
      tableName: meta.tableName,
      type: meta.columnType,
      displayType: getDisplayTypeFromDataType(typeStringMap[meta.columnType]),
    };
  };

  const normalizeJoins = (joins: JoinRow[]) =>
    joins.map((join) => {
      let leftPayload = getColumnInfo(join.leftColumn, join.leftTable);
      if (!leftPayload && join.leftColumn) {
        const leftFallback = selectedColumnsData?.find((c) => c.name === join.leftColumn);
        if (leftFallback) {
          leftPayload = {
            name: leftFallback.name,
            tableName: leftFallback.tableName,
            type: leftFallback.columnType,
            displayType: getDisplayTypeFromDataType(typeStringMap[leftFallback.columnType]),
          };
        }
      }

      let rightPayload = getColumnInfo(join.rightColumn, join.rightTable);
      if (!rightPayload && join.rightColumn) {
        const rightFallback = selectedColumnsData?.find((c) => c.name === join.rightColumn);
        if (rightFallback) {
          rightPayload = {
            name: rightFallback.name,
            tableName: rightFallback.tableName,
            type: rightFallback.columnType,
            displayType: getDisplayTypeFromDataType(typeStringMap[rightFallback.columnType]),
          };
        }
      }

      return {
        column: leftPayload,
        joinColumn: rightPayload,
        tableName: leftPayload?.tableName ?? join.leftTable,
      };
    });

  const normalizeFilters = (filters: FilterItem[]) =>
    filters.map((filter, index) => {
      let colName =
        typeof filter.column === 'object' && filter.column !== null
          ? (filter.column as unknown as { name: string }).name
          : filter.column;

      let tbl = selectedTable;

      if (typeof colName === 'string') {
        const { table, column } = parseQualifiedColumn(colName);
        if (table) {
          tbl = table;
          colName = column;
        }
      }

      const meta = selectedColumnsData?.find((c) => c.name === colName && c.tableName === tbl);

      return {
        column: {
          name: meta?.name,
          tableName: meta?.tableName,
          displayName: meta?.name,
          displayType: getDisplayTypeFromDataType(typeStringMap[meta?.columnType ?? 0]),
          type: meta?.columnType,
        },
        condition: filter.condition,
        isLocked: filter.locked,
        value: filter.value,
        ...(index > 0 && filter.operator ? { operator: filter.operator } : {}),
      };
    });

  const buildDisplayColumns = (viz: VisualizationFormState) => {
    let displayColumns: Record<string, unknown>[] = [];
    let distributionColumn: { name: string; tableName: string; columnType: number };

    if (viz.type === 'Table') {
      displayColumns = viz.columns
        .map((colName: string) => {
          const { table, column: col } = parseQualifiedColumn(String(colName));
          const tbl = table ?? selectedTable;
          const meta = selectedColumnsData?.find((c) => c.name === col && c.tableName === tbl);
          if (!meta) {
            return null;
          }
          return { ...getColumnInfo(meta.name, meta.tableName), displayName: meta.name };
        })
        .filter(Boolean) as Record<string, unknown>[];

      const first = viz.columns?.[0] ?? '';
      const { table: firstTable, column: firstCol } = parseQualifiedColumn(String(first));
      const tbl = firstTable ?? selectedTable;
      const meta = selectedColumnsData?.find((c) => c.name === firstCol && c.tableName === tbl);
      distributionColumn = {
        name: meta?.name ?? firstCol,
        tableName: meta?.tableName ?? tbl ?? '',
        columnType: meta?.columnType ?? 0,
      };
    } else {
      const extractRawColumnName = (fullName: string): string => {
        const parts = fullName.trim().split(' ');
        return parts[parts.length - 1] ?? fullName;
      };

      const rawColumnName = extractRawColumnName(viz.column ?? '');
      const isCount = viz.column === 'Count';
      const columnMeta = selectedColumnsData?.find((c) => c.name === rawColumnName && c.tableName === selectedTable);

      const matchingAggregate = savedAggregates.find((agg) => {
        const funcName = typeof agg.func === 'string' ? agg.func : '';
        const funcMatches = funcName.toLowerCase() === viz.column?.split(' ')[0]?.toLowerCase();
        if (funcName === 'Count') {
          return funcMatches;
        }
        const colMatches = agg.column ? agg.column.toLowerCase() === rawColumnName.toLowerCase() : true;
        return funcMatches && colMatches;
      });

      const aggregateFnType = matchingAggregate ? getAggregateFunctionNumber(matchingAggregate.func) : undefined;

      if (isCount) {
        displayColumns = [
          {
            name: 'Count',
            tableName: selectedTable,
            type: 3,
            displayType: 'BIGINT',
            displayName: viz.column,
            aggregateFunctionType: aggregateFnType,
          },
        ];
      } else if (columnMeta) {
        displayColumns = [
          {
            ...getColumnInfo(columnMeta.name, columnMeta.tableName),
            displayName: viz.column,
            aggregateFunctionType: aggregateFnType,
          },
        ];
      } else {
        displayColumns = [
          {
            name: rawColumnName,
            tableName: selectedTable,
            type: 3,
            displayType: 'BIGINT',
            displayName: viz.column,
            aggregateFunctionType: aggregateFnType,
          },
        ];
      }

      distributionColumn = columnMeta
        ? { name: columnMeta.name, tableName: columnMeta.tableName, columnType: columnMeta.columnType }
        : { name: isCount ? 'Count' : rawColumnName, tableName: selectedTable ?? '', columnType: 0 };
    }

    return { displayColumns, distributionColumn };
  };
  const [savedAggregates, setSavedAggregates] = useState<AggregateItem[]>([]);
  const [savedFilters, setSavedFilters] = useState<FilterItem[]>([]);

  const adaptSorting = (
    sorting?: SortingRule[] | null,
    table?: string
  ): { table: string; column: string; direction: SortDirection; displayName: string } | null => {
    if (!sorting?.[0] || !table) {
      return null;
    }

    const sort = sorting[0];

    return {
      table,
      column: sort.column.name,
      direction: sort.direction.toString() as SortDirection,
      displayName: sort.column.displayName || '',
    };
  };

  const [sorting, setSorting] = useState<{
    table: string;
    column: string;
    direction: SortDirection;
    displayName: string;
  } | null>(adaptSorting(query?.sorting, query?.tableName));

  const uniqueTableOptions = useMemo(() => {
    if (!selectedColumnsData) {
      return [];
    }

    const uniqueTables = Array.from(new Set(selectedColumnsData.map((col) => col.tableName))).map((name) => ({ name }));
    const sortedTables = sortTablesByUpperCaseFirst(uniqueTables);

    return sortedTables.map((table) => ({
      label: table.name,
      value: table.name,
    }));
  }, [selectedColumnsData]);

  const getSectionLabel = (id: ReportFilterSectionId | null) =>
    REPORT_FILTER_SECTIONS.find((section) => section.id === id)?.label ?? '';

  const onSubmitGrouping = (column: string) => {
    if (!column || !selectedTable) {
      return;
    }

    const columnMeta = selectedColumnsData?.find((col) => col.name === column && col.tableName === selectedTable);
    if (!columnMeta) {
      return;
    }

    setGrouping({
      name: columnMeta.name,
      displayName: columnMeta.name,
      tableName: columnMeta.tableName,
      type: columnMeta.columnType,
      displayType: typeStringMap[columnMeta.columnType],
    });

    setActiveModal(null);
  };

  const onSubmitAggregates = (newAggregates: { func: AggregateFunctionType; column?: string }[]) => {
    const aggregatesWithId: AggregateItem[] = newAggregates.map((agg) => ({
      id: getNextId(),
      func: agg.func,
      column: agg.column ?? null,
    }));
    setSavedAggregates(aggregatesWithId);
    setAggregates(newAggregates);
    setActiveModal(null);
  };

  const onSubmitFilters = (newFilters: Omit<FilterItem, 'id'>[]) => {
    const filtersWithId: FilterItem[] = newFilters.map((filter, index) => ({
      id: String(getNextId()),
      column: filter.column,
      condition: filter.condition,
      value: filter.value ?? '',
      locked: filter.locked ?? false,
      operator: index > 0 ? filter.operator : undefined,
    }));

    setSavedFilters(filtersWithId);
    setActiveModal(null);
  };

  const onSubmitSortBy = (column: string, direction: SortDirection) => {
    if (!column || !selectedTable) {
      return;
    }

    setSorting({ table: selectedTable, column, direction, displayName: column });
    setActiveModal(null);
  };

  const clearForm = () => {
    setActiveModal(null);
    setSavedJoins([]);
    setGrouping(null);
    setSorting(null);
    setAggregates([]);
    setSavedAggregates([]);
    setSavedFilters([]);
    setVisualization(null);
  };

  const resetForm = () => {
    clearForm();
    reset({
      selectedTable: null,
    });
  };

  const getCurrentConfiguration = (): Record<string, unknown> | null => {
    if (!selectedTable || !visualization) {
      return null;
    }

    const payload: Record<string, unknown> = {
      tableName: selectedTable,
      visualization,
    };

    const apiJoins = savedJoins.length ? savedJoins.map((j) => joinRowToApiJoin(j)) : [];

    const query: Record<string, unknown> = {
      tableName: selectedTable,
      joins: apiJoins,
      filters: savedFilters.length
        ? savedFilters.map((f) => ({
            ...f,
            operator: f.operator === 'And' ? FilterOperator.And : FilterOperator.Or,
          }))
        : [],
    };

    const aggregatesResult = buildAggregatesPayload(savedAggregates, selectedTable);
    if (aggregatesResult.displayColumns.length > 0) {
      query.displayColumns = aggregatesResult.displayColumns;
      payload.aggregates = aggregatesResult.aggregates;
    } else {
      query.displayColumns = [];
    }

    const vizCols = buildVizDisplayColumnsForCurrent(visualization);
    if (vizCols.length > 0) {
      query.displayColumns = vizCols;
    }

    payload.query = query;

    if (savedJoins.length > 0) {
      payload.joins = savedJoins;
    }
    if (grouping) {
      payload.groupBy = [grouping];
    }
    if (savedFilters.length > 0) {
      payload.filters = savedFilters;
    }

    const sortingPayload = buildSortingPayload();
    if (sortingPayload) {
      payload.sorting = sortingPayload;
    }

    if (visualization.type !== 'Table' && 'timelapseType' in visualization) {
      payload.timelapseType = visualization.timelapseType;
    }

    return payload;
  };

  const modalContentMap: Record<ReportFilterSectionId, React.ReactNode> = {
    [ReportFilterSectionId.JOIN]: (
      <CreateReportTemplateJoinModal
        selectedColumnsData={selectedColumnsData}
        selectedTable={selectedTable}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={(joins: JoinRow[]) => {
          setSavedJoins(joins);
          setActiveModal(null);
        }}
        selectedJoins={savedJoins}
      />
    ),
    [ReportFilterSectionId.GROUPING]: (
      <CreateReportTemplateGroupingModal
        control={control}
        selectedColumnsData={selectedColumnsData ?? []}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitGrouping}
        selectedGroupingColumns={selectedColumnsData?.map((col) => col.name) ?? []}
        selectedGroupingColumnName={grouping?.name}
      />
    ),
    [ReportFilterSectionId.AGGREGATE]: (
      <CreateReportTemplateAggregateModal
        control={control}
        selectedColumnsData={selectedColumnsData}
        onClose={() => setActiveModal(null)}
        initialAggregates={aggregates}
        onSubmit={onSubmitAggregates}
      />
    ),
    [ReportFilterSectionId.FILTER]: (
      <CreateReportTemplateFiltersModal
        control={control}
        selectedColumnsData={selectedColumnsData}
        selectedTable={selectedTable}
        grouping={grouping}
        aggregates={aggregates}
        initialFilters={savedFilters.map((filter) => {
          const col = filter.column as unknown as VisualizationItemPayload | string | undefined;
          return {
            ...filter,
            column: typeof col === 'object' && col !== null ? col.name : (col as string) ?? null,
            displayName: typeof col === 'object' && col !== null ? col.displayName : (col as string) ?? null,
          };
        })}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitFilters}
      />
    ),
    [ReportFilterSectionId.SORT]: (
      <CreateReportTemplateSortByModal
        control={control}
        selectedColumnsData={selectedColumnsData ?? []}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitSortBy}
        savedAggregates={savedAggregates}
        initialSorting={sorting ?? undefined}
      />
    ),
  };

  const handleFilterItemClick = (id: ReportFilterSectionId) => {
    setActiveModal(id);
  };

  const applyTableAndSortingFromQuery = (dataQuery?: ReportTemplateConfiguration['query']) => {
    if (!dataQuery?.tableName) {
      return;
    }

    setValue('selectedTable', {
      label: dataQuery.tableName,
      value: dataQuery.tableName,
    });

    const sortingArr = dataQuery?.sorting ?? [];
    if (sortingArr.length > 0) {
      const sort = sortingArr[0];
      setSorting({
        table: sort.column.tableName,
        column: sort.column.name,
        direction: sort.direction === 1 ? ASCENDING_DIRECTION : DESCENDING_DIRECTION,
        displayName: sort.column.displayName || '',
      });

      setValue('sorting', {
        label: sort.column.name,
        value: sort.column.name,
      });

      setValue('sortingDirection', {
        label: sort.direction === 1 ? 'Ascending' : 'Descending',
        value: sort.direction === 1 ? ASCENDING_DIRECTION : DESCENDING_DIRECTION,
      });
    }
  };

  const applyJoinsFromQuery = (joinsArray: ApiJoinRow[] | undefined, tableName?: string) => {
    if (Array.isArray(joinsArray) && joinsArray.length > 0) {
      setSavedJoins(joinsArray.map((j, i) => convertApiJoinToJoinRow(j, i, tableName)));
    }
  };

  const applyFiltersFromQuery = (filtersArray: QueryFilter[] | undefined) => {
    if (Array.isArray(filtersArray) && filtersArray.length > 0) {
      const adapted: FilterItem[] = filtersArray.map((filter, index) => ({
        id: filter.id || String(getNextId()),
        column: filter.column?.name || '',
        condition: filter.condition,
        value: filter.value ?? '',
        locked: filter.isLocked ?? false,
        operator: index > 0 ? getOperatorString(filter.operator) : undefined,
      }));

      setSavedFilters(adapted);
    }
  };

  const applyAggregatesFromQuery = (displayColumns: DisplayColumn[] | undefined) => {
    if (!Array.isArray(displayColumns)) {
      return;
    }

    const foundAggregates = displayColumns
      .filter((col) => {
        const hasAggregateType = col.aggregateFunctionType != null;
        const hasAggregateDisplay = detectAggregateFunction(col.displayName) != null;
        const isCalculatedAggregate = Boolean(col.displayName && col.name && col.displayName !== col.name);
        return hasAggregateType || hasAggregateDisplay || isCalculatedAggregate;
      })
      .map((col) => {
        let func = col.aggregateFunctionType ? getAggregateFunctionTypeString(col.aggregateFunctionType) : null;
        if (!func) {
          const detected = detectAggregateFunction(col.displayName);
          if (detected) {
            func = detected;
          }
        }

        return {
          func: func || 'Sum of',
          column: func === 'Count' || col.displayName === 'Count' ? 'Count' : col.name,
        };
      });

    if (foundAggregates.length > 0) {
      const adaptedForState = foundAggregates.map((agg) => ({
        func: agg.func,
        column: agg.func === 'Count' || agg.column === 'Count' ? undefined : agg.column,
      }));

      setSavedAggregates(
        foundAggregates.map((agg) => ({
          id: getNextId(),
          func: agg.func,
          column: agg.func === 'Count' || agg.column === 'Count' ? null : agg.column,
        }))
      );
      setAggregates(adaptedForState);
    }
  };

  const setInitialConfiguration = (data: ReportTemplateConfiguration) => {
    const dataQuery = data.query;

    applyTableAndSortingFromQuery(dataQuery);
    applyJoinsFromQuery(dataQuery?.joins, dataQuery?.tableName);
    applyFiltersFromQuery(dataQuery?.filters);
    applyAggregatesFromQuery(dataQuery?.displayColumns);

    if (dataQuery?.groupBy) {
      setGrouping(dataQuery.groupBy);
    }

    if (data.name) {
      setTemplateName(data.name);
    }

    setCount((prev) => prev + 1);
  };

  const getGroupByColumnForSave = () => {
    return grouping ? getColumnInfo(grouping.name, grouping.tableName) : undefined;
  };

  const getNormalizedSortingForSave = () => {
    if (!sorting) {
      return undefined;
    }
    return [
      {
        column: getColumnInfo(sorting.column, selectedTable ?? '') || {
          name: sorting.column,
          tableName: selectedTable ?? '',
          type: 3,
          displayType: 'BIGINT',
        },
        direction: sorting.direction === ASCENDING_DIRECTION ? 1 : 2,
        order: 1,
      },
    ];
  };

  const buildQueryPayload = (
    displayCols: Record<string, unknown>[],
    joins: unknown[],
    filters: unknown[],
    groupByCol?: unknown,
    sortingArr?: unknown,
    rowLimit?: number
  ) => {
    const q: Record<string, unknown> = {
      tableName: selectedTable,
      joins,
      filters,
      displayColumns: displayCols,
    };
    if (groupByCol) {
      q.groupBy = groupByCol;
    }
    if (sortingArr) {
      q.sorting = sortingArr;
    }
    if (rowLimit !== undefined) {
      q.rowLimit = rowLimit;
    }
    return q;
  };

  const buildSavePayload = (saveName: string) => {
    if (!selectedTable || !visualization) {
      return null;
    }

    const normalizedJoins = normalizeJoins(savedJoins);
    const normalizedFilters = normalizeFilters(savedFilters);
    const normalizedSorting = getNormalizedSortingForSave();
    const groupByColumn = getGroupByColumnForSave();
    const { displayColumns, distributionColumn } = buildDisplayColumns(visualization);

    const queryPayload = buildQueryPayload(
      displayColumns,
      normalizedJoins,
      normalizedFilters,
      groupByColumn,
      normalizedSorting,
      visualization.type === 'Table' ? visualization.rowLimit : undefined
    );

    return {
      name: saveName,
      distributionColumn,
      query: queryPayload,
      visualizationTypeId: visualizationTypeMap[visualization.type as VisualizationType],
    };
  };

  useImperativeHandle(ref, () => ({
    resetForm,
    getCurrentConfiguration,
    getSelectedColumnsData: () => selectedColumnsData ?? [],
    getTemplateName: () => templateName,
    setInitialConfiguration,
    buildSavePayload,
  }));

  useEffect(() => {
    if (initialVisualization) {
      setVisualization(initialVisualization);
    }

    if (initialValues) {
      setInitialConfiguration(initialValues);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const isVisualizationValid = (() => {
      if (!visualization) {
        return false;
      }

      if (visualization.type === 'Table') {
        return Array.isArray(visualization.columns) && visualization.columns.length > 0;
      }

      return typeof visualization.column === 'string' && visualization.column.trim() !== '';
    })();

    const isValid = Boolean(selectedTable && visualization?.type && isVisualizationValid);

    onValidationChange?.(isValid);
  }, [selectedTable, visualization, onValidationChange]);

  return (
    <div className="w-[23.875rem] p-6 overflow-auto">
      <FormProvider {...createReportTemplateMethods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <SelectFormInput
            dataCy="expiring-subscription-select"
            control={control as unknown as Control}
            onSelectValue={(value) => {
              const newValue = value?.value ?? null;
              if (newValue !== selectedTable) {
                clearForm();
                setVisualization(null);
                reset({
                  selectedTable: newValue ? { label: newValue, value: newValue } : null,
                  grouping: null,
                  sorting: null,
                  sortingDirection: null,
                  filters: [],
                  visualization: null,
                  name: watchedTable?.value === newValue ? watch('name') : '',
                });
                setCount((key) => key + 1);
              }
            }}
            name="selectedTable"
            options={uniqueTableOptions}
            label={t('SelectTable')}
            labelClassName="mb-2 text-black-900 dark:text-white-00"
            required
            placeholder={t('SelectTable')}
            isClearable
          />
          {selectedTable && (
            <div className="mt-4">
              {REPORT_FILTER_SECTIONS.map(({ id, label }) => (
                <CreateReportTemplateFilterItem
                  key={id}
                  title={label}
                  onClick={() => handleFilterItemClick(id)}
                  joins={id === 'join' ? savedJoins : undefined}
                  grouping={
                    id === 'grouping' && grouping ? { table: grouping.tableName, column: grouping.name } : undefined
                  }
                  aggregates={
                    id === 'aggregate'
                      ? savedAggregates.map((agg) => {
                          return {
                            func: getAggregateFunctionTypeString(agg.func),
                            column: agg.column || undefined,
                          };
                        })
                      : undefined
                  }
                  filters={id === 'filter' ? savedFilters : undefined}
                  selectedColumnsData={selectedColumnsData}
                  selectedTable={selectedTable}
                  sorting={id === 'sort' ? sorting : undefined}
                  onDeleteJoin={(joinId: number) =>
                    setSavedJoins((prev) => prev.filter((joins) => joins.id !== joinId))
                  }
                  onDeleteGroupings={() => setGrouping(null)}
                  onDeleteAggregate={(index) => {
                    setSavedAggregates((prev) => prev.filter((_, i) => i !== index));
                    setAggregates((prev) => prev.filter((_, i) => i !== index));
                  }}
                  onDeleteFilter={(filterId: string) => {
                    setSavedFilters((prev) => prev.filter((filtered) => filtered.id !== filterId));
                  }}
                  onDeleteSorting={() => setSorting(null)}
                />
              ))}

              <CreateReportTemplateVisualizationBlock
                key={count}
                control={control}
                selectedTable={selectedTable}
                selectedColumnsData={selectedColumnsData ?? []}
                onChange={setVisualization}
                groupingColumn={grouping ? [grouping.name] : []}
                aggregateColumns={savedAggregates
                  .filter((agg) => agg.func && (agg.func === 'Count' || agg.column))
                  .map((agg) => ({
                    label: agg.func === 'Count' ? 'Count' : `${agg.func} ${agg.column}`,
                    value: agg.func === 'Count' ? 'Count' : `${agg.func} ${agg.column}`,
                    rawColumn: agg.column ?? undefined,
                  }))}
                initialVisualization={initialVisualization}
                selectedVisualizationType={visualization?.type ?? null}
                selectedVisualizationColumn={(() => {
                  const column = visualization?.type !== 'Table' ? visualization?.column ?? '' : '';
                  return column;
                })()}
                selectedVisualizationColumns={visualization?.type === 'Table' ? visualization.columns : []}
                selectedTimelapseType={visualization?.type !== 'Table' ? visualization?.timelapseType : undefined}
                selectedVisualizationRowLimit={
                  visualization?.type === 'Table'
                    ? (visualization as { type: 'Table'; columns: string[]; rowLimit?: number }).rowLimit
                    : query?.rowLimit
                }
              />
            </div>
          )}

          <Modal
            key={activeModal}
            bodyClassName="!w-auto"
            isOpen={!!activeModal}
            onClose={() => setActiveModal(null)}
            heading={t(getSectionLabel(activeModal))}
          >
            {activeModal && modalContentMap[activeModal]}
          </Modal>
        </form>
      </FormProvider>
    </div>
  );
};

export default React.forwardRef(CreateReportTemplateFiltersBlock);
